import type { CSSProperties, ReactNode } from 'react';
import styles from './MarketingPhoneFrame.module.css';

type MarketingPhoneFrameProps = {
  /** CSS scale factor (e.g. 0.96 for hero phone, 0.68 for plan-showcase). */
  scale?: number;
  /** Optional className on the outer shell (for landing-page positioning). */
  className?: string;
  /** Inner phone content. MUST NOT contain real interactive elements
   *  (no <button>, <a>, <input>) — these previews are decorative
   *  marketing imagery, aria-hidden from assistive tech. */
  children: ReactNode;
};

/**
 * Decorative phone-bezel wrapper used by landing-page marketing
 * previews. NOT a product component — see naming convention in the
 * Phase 3 plan: anything under `MarketingPreview/` is presentation-
 * only and must not be reused in product surfaces with real data.
 *
 * Marked `aria-hidden="true"` because the visual content inside is
 * marketing imagery, not consumable info. Children must not include
 * keyboard-focusable elements (rule enforced by code review per the
 * Phase 3 plan; see the a11y section).
 *
 * The calc-based scaling pattern (outer dim = base × scale) ensures
 * the layout box matches the visual box — no whitespace gaps that
 * `transform: scale()` alone would leave.
 */
export function MarketingPhoneFrame({
  scale = 1,
  className,
  children,
}: MarketingPhoneFrameProps) {
  const style: CSSProperties & Record<string, string> = {
    '--phone-scale': String(scale),
  };
  return (
    <div
      className={[styles.shell, className].filter(Boolean).join(' ')}
      style={style}
      aria-hidden="true"
    >
      <div className={styles.inner}>{children}</div>
    </div>
  );
}

/**
 * Fake iOS-style status bar used at the top of every phone preview.
 * Decorative; no real time/battery data.
 */
export function MarketingPhoneStatusBar() {
  return (
    <div className={styles.statusBar}>
      <span>9:41</span>
      <span className={styles.statusBattery}>●●● 5G ▣</span>
    </div>
  );
}
